import React, { useState, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { compressIfImage } from '../utils/imageCompress';

const VEHICLE_TYPES = ['economy', 'comfort', 'business', 'premium', 'vip', 'suv', 'van', 'minibus', 'bus'];
const REGIONS = ['hurghada', 'sharm', 'cairo', 'luxor', 'aswan', 'marsa_alam', 'alexandria', 'gouna'];
const LANG_CODES = ['ar', 'en', 'de', 'fr', 'it', 'es', 'ru', 'tr', 'nl', 'pt', 'sv', 'no', 'zh'];

// --- Registration Screen ---
export function RegisterScreen({ t, Icons, onSwitchToLogin, language, setLanguage }) {
  const { register, verifyEmail, resendCode } = useAuthStore();
  const [step, setStep] = useState('form'); // form | verify
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setError(t('auth_fill_all')); return; }
    if (!agbAccepted) { setError('Please accept the Terms and Conditions to proceed.'); return; }
    setLoading(true); setError('');
    try {
      const result = await register(form.email, form.password, form.name, form.phone);
      if (result?.requires_verification) setStep('verify');
    } catch (err) { setError(err.response?.data?.detail || t('common_error')); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { await verifyEmail(form.email, code); }
    catch (err) { setError(err.response?.data?.detail || 'Code ungültig'); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3.5 text-base focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-500";

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="pwa-register-screen">
      <div className="flex-1 flex flex-col justify-center px-6 pb-12">
        <div className="text-center mb-8">
          <div className="text-5xl font-black text-orange-500 mb-1">366</div>
          <div className="text-lg font-semibold text-white tracking-wide">DRIVER</div>
          <p className="text-slate-400 text-sm mt-3">{t('auth_register_title')}</p>
        </div>

        {step === 'verify' ? (
          <form onSubmit={handleVerify} className="space-y-4 max-w-sm mx-auto w-full">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg text-center">{error}</div>}
            <p className="text-slate-400 text-sm text-center">Code wurde an {form.email} gesendet</p>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3.5 text-center text-2xl tracking-widest focus:border-orange-500 outline-none" data-testid="pwa-verify-code" autoFocus required />
            <button type="submit" disabled={loading} className="w-full bg-orange-500 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl disabled:opacity-50" data-testid="pwa-verify-submit">
              {loading ? t('common_loading') : t('common_confirm')}
            </button>
            <button type="button" onClick={async () => { try { await resendCode(form.email); } catch {} }} className="w-full text-slate-400 text-sm py-2">
              Code erneut senden
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-3 max-w-sm mx-auto w-full">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg text-center" data-testid="pwa-register-error">{error}</div>}
            <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder={t('auth_name')} className={inputCls} data-testid="pwa-register-name" required />
            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder={t('auth_email')} className={inputCls} data-testid="pwa-register-email" required />
            <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder={t('auth_phone')} className={inputCls} data-testid="pwa-register-phone" />
            <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder={t('auth_password')} className={inputCls} data-testid="pwa-register-password" required />
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="pwa-register-agb-label">
              <input type="checkbox" checked={agbAccepted} onChange={(e) => setAgbAccepted(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500 shrink-0" data-testid="pwa-register-agb-checkbox" />
              <span className="text-xs text-slate-400 leading-relaxed">
                I accept the <a href="/fahrer-agb" target="_blank" rel="noopener noreferrer" className="text-orange-400 underline">Driver Terms and Conditions</a> and the <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-orange-400 underline">General Terms and Conditions</a> of 366Transfer.
              </span>
            </label>
            <button type="submit" disabled={loading || !agbAccepted} className="w-full bg-orange-500 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl disabled:opacity-50" data-testid="pwa-register-submit">
              {loading ? t('common_loading') : t('auth_register_btn')}
            </button>
          </form>
        )}
        <button onClick={onSwitchToLogin} className="text-orange-400 text-sm text-center mt-5" data-testid="pwa-switch-login">{t('auth_switch_to_login')}</button>
      </div>
    </div>
  );
}

// --- Driver Setup (Register as driver + first vehicle) ---
export function DriverSetupScreen({ t, Icons, onComplete }) {
  const [step, setStep] = useState(1); // 1=type, 2=info, 3=vehicle, 4=done
  const [isCompany, setIsCompany] = useState(false);
  const [info, setInfo] = useState({ region: 'hurghada', languages: ['ar', 'en'], company_name: '' });
  const [vehicle, setVehicle] = useState({ make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleLang = (code) => {
    setInfo(prev => ({ ...prev, languages: prev.languages.includes(code) ? prev.languages.filter(l => l !== code) : [...prev.languages, code] }));
  };

  const handleSubmit = async () => {
    if (!vehicle.make || !vehicle.model || !vehicle.plate_number) { setError(t('auth_fill_all')); return; }
    setLoading(true); setError('');
    try {
      await api.post('/driver/register', {
        country: 'EG', region: info.region, vehicle,
        is_company: isCompany, company_name: isCompany ? info.company_name : undefined,
        languages: info.languages,
      });
      setStep(4);
      setTimeout(() => onComplete(), 2000);
    } catch (err) { setError(err.response?.data?.detail || t('common_error')); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none placeholder:text-slate-500";
  const selectCls = "w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none";

  if (step === 4) {
    return (
      <div className="text-center py-20" data-testid="pwa-setup-success">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-400"><Icons.Check /></div>
        <h2 className="text-white text-xl font-semibold mb-2">{t('company_success') || 'Registriert!'}</h2>
        <p className="text-slate-400 text-sm">{t('driver_pending_desc')}</p>
      </div>
    );
  }

  return (
    <div className="pb-4" data-testid="pwa-driver-setup">
      {/* Progress */}
      <div className="flex gap-1 mb-6">
        {[1,2,3].map(s => <div key={s} className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-orange-500' : 'bg-slate-700'}`} />)}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg mb-4">{error}</div>}

      {step === 1 && (
        <div data-testid="pwa-setup-step1">
          <h2 className="text-white text-lg font-semibold mb-1">{t('auth_register_title')}</h2>
          <p className="text-slate-400 text-sm mb-6">{t('nav_for_drivers')} / {t('nav_for_companies')}</p>
          <div className="space-y-3">
            <button onClick={() => { setIsCompany(false); setStep(2); }} className="w-full bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-left active:bg-slate-800 flex items-center gap-4" data-testid="pwa-type-driver">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-400"><Icons.User /></div>
              <div><div className="text-white font-medium">{t('nav_for_drivers')}</div><div className="text-xs text-slate-400">{t('landing_driver_cta_desc')}</div></div>
            </button>
            <button onClick={() => { setIsCompany(true); setStep(2); }} className="w-full bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-left active:bg-slate-800 flex items-center gap-4" data-testid="pwa-type-company">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400"><Icons.Users /></div>
              <div><div className="text-white font-medium">{t('nav_for_companies')}</div><div className="text-xs text-slate-400">{t('company_subtitle')}</div></div>
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="pwa-setup-step2">
          <h2 className="text-white text-lg font-semibold mb-4">{isCompany ? t('company_title') : t('profile_driver')}</h2>
          <div className="space-y-3">
            {isCompany && <input type="text" value={info.company_name} onChange={(e) => setInfo({...info, company_name: e.target.value})} placeholder={t('company_name')} className={inputCls} data-testid="pwa-company-name" />}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t('company_region')}</label>
              <select value={info.region} onChange={(e) => setInfo({...info, region: e.target.value})} className={selectCls} data-testid="pwa-setup-region">
                {REGIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('_',' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">{t('company_languages')}</label>
              <div className="flex flex-wrap gap-2">
                {LANG_CODES.map(c => (
                  <button key={c} type="button" onClick={() => toggleLang(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${info.languages.includes(c) ? 'bg-orange-500 text-white' : 'bg-slate-700 text-slate-400'}`} data-testid={`pwa-lang-${c}`}>
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setStep(3)} className="w-full bg-orange-500 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl mt-2" data-testid="pwa-setup-next">{t('company_next_vehicles')}</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="pwa-setup-step3">
          <h2 className="text-white text-lg font-semibold mb-4">{t('company_add_vehicle')}</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="text" value={vehicle.make} onChange={(e) => setVehicle({...vehicle, make: e.target.value})} placeholder={t('profile_vehicle_make')} className={inputCls} data-testid="pwa-v-make" required />
              <input type="text" value={vehicle.model} onChange={(e) => setVehicle({...vehicle, model: e.target.value})} placeholder={t('profile_vehicle_model')} className={inputCls} data-testid="pwa-v-model" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" value={vehicle.year} onChange={(e) => setVehicle({...vehicle, year: parseInt(e.target.value) || 2024})} placeholder={t('profile_vehicle_year')} className={inputCls} data-testid="pwa-v-year" />
              <input type="text" value={vehicle.color} onChange={(e) => setVehicle({...vehicle, color: e.target.value})} placeholder={t('profile_vehicle_color')} className={inputCls} data-testid="pwa-v-color" />
            </div>
            <input type="text" value={vehicle.plate_number} onChange={(e) => setVehicle({...vehicle, plate_number: e.target.value})} placeholder={t('profile_vehicle_plate')} className={inputCls} data-testid="pwa-v-plate" required />
            <select value={vehicle.vehicle_type} onChange={(e) => setVehicle({...vehicle, vehicle_type: e.target.value})} className={selectCls} data-testid="pwa-v-type">
              {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t('profile_vehicle_passengers')}</label>
                <input type="number" value={vehicle.passengers} onChange={(e) => setVehicle({...vehicle, passengers: parseInt(e.target.value) || 1})} className={inputCls} data-testid="pwa-v-passengers" min="1" max="50" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t('profile_vehicle_luggage')}</label>
                <input type="number" value={vehicle.luggage} onChange={(e) => setVehicle({...vehicle, luggage: parseInt(e.target.value) || 0})} className={inputCls} data-testid="pwa-v-luggage" min="0" max="50" />
              </div>
            </div>
            <button onClick={handleSubmit} disabled={loading} className="w-full bg-orange-500 active:bg-orange-700 text-white font-semibold py-3.5 rounded-xl mt-2 disabled:opacity-50" data-testid="pwa-setup-submit">
              {loading ? t('common_loading') : t('company_submit')}
            </button>
          </div>
          <button onClick={() => setStep(2)} className="w-full text-slate-400 text-sm py-3 mt-1">{t('common_back')}</button>
        </div>
      )}
    </div>
  );
}

// --- Vehicle Management Screen ---
export function VehicleManageScreen({ t, Icons, driverProfile, onBack, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [form, setForm] = useState({ make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const vehicles = driverProfile?.vehicles || [];

  const resetForm = () => setForm({ make: '', model: '', year: 2024, color: '', plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3 });

  const handleAdd = async () => {
    if (!form.make || !form.model || !form.plate_number) { setError(t('auth_fill_all')); return; }
    setLoading(true); setError('');
    try { await api.post('/driver/vehicle/add', form); resetForm(); setShowAdd(false); onRefresh(); }
    catch (err) { setError(err.response?.data?.detail || t('common_error')); }
    finally { setLoading(false); }
  };

  const handleUpdate = async () => {
    if (!form.make || !form.model || !form.plate_number) { setError(t('auth_fill_all')); return; }
    setLoading(true); setError('');
    try { await api.put(`/driver/vehicle/${editVehicle.id}`, form); setEditVehicle(null); resetForm(); onRefresh(); }
    catch (err) { setError(err.response?.data?.detail || t('common_error')); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common_delete') + '?')) return;
    try { await api.delete(`/driver/vehicle/${id}`); onRefresh(); }
    catch {}
  };

  const startEdit = (v) => {
    setForm({ make: v.make, model: v.model, year: v.year, color: v.color, plate_number: v.plate_number, vehicle_type: v.vehicle_type, passengers: v.passengers, luggage: v.luggage });
    setEditVehicle(v);
    setShowAdd(false);
  };

  const inputCls = "w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none placeholder:text-slate-500";
  const selectCls = "w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none";

  const renderForm = (onSubmit, submitLabel) => (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 mb-4 space-y-3" data-testid="pwa-vehicle-form">
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-2.5 rounded-lg">{error}</div>}
      <div className="grid grid-cols-2 gap-3">
        <input type="text" value={form.make} onChange={(e) => setForm({...form, make: e.target.value})} placeholder={t('profile_vehicle_make')} className={inputCls} data-testid="pwa-vm-make" />
        <input type="text" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} placeholder={t('profile_vehicle_model')} className={inputCls} data-testid="pwa-vm-model" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input type="number" value={form.year} onChange={(e) => setForm({...form, year: parseInt(e.target.value) || 2024})} placeholder={t('profile_vehicle_year')} className={inputCls} />
        <input type="text" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} placeholder={t('profile_vehicle_color')} className={inputCls} />
      </div>
      <input type="text" value={form.plate_number} onChange={(e) => setForm({...form, plate_number: e.target.value})} placeholder={t('profile_vehicle_plate')} className={inputCls} data-testid="pwa-vm-plate" />
      <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} className={selectCls}>
        {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-slate-400 mb-1 block">{t('profile_vehicle_passengers')}</label><input type="number" value={form.passengers} onChange={(e) => setForm({...form, passengers: parseInt(e.target.value)||1})} className={inputCls} min="1" max="50" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">{t('profile_vehicle_luggage')}</label><input type="number" value={form.luggage} onChange={(e) => setForm({...form, luggage: parseInt(e.target.value)||0})} className={inputCls} min="0" max="50" /></div>
      </div>
      <div className="flex gap-2">
        <button onClick={onSubmit} disabled={loading} className="flex-1 bg-orange-500 active:bg-orange-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 text-sm" data-testid="pwa-vm-submit">
          {loading ? t('common_loading') : submitLabel}
        </button>
        <button onClick={() => { setShowAdd(false); setEditVehicle(null); resetForm(); setError(''); }} className="px-4 bg-slate-700 text-slate-300 rounded-xl text-sm">{t('common_cancel')}</button>
      </div>
    </div>
  );

  return (
    <div className="pb-4" data-testid="pwa-vehicle-manage">
      <button onClick={onBack} className="flex items-center gap-1 text-slate-400 mb-4 active:text-white"><Icons.ChevronLeft />{t('common_back')}</button>
      <h2 className="text-white font-semibold mb-4">{t('profile_fleet')} ({vehicles.length}/4)</h2>

      {editVehicle && renderForm(handleUpdate, t('common_save'))}
      {showAdd && !editVehicle && renderForm(handleAdd, t('company_add_vehicle'))}

      {vehicles.map((v) => (
        <div key={v.id} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 mb-3" data-testid={`pwa-vehicle-${v.id}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-slate-400"><Icons.Car /></div>
            <div className="flex-1">
              <div className="text-sm text-white font-medium">{v.make} {v.model} ({v.year})</div>
              <div className="text-xs text-slate-500">{v.vehicle_type} | {v.color} | {v.plate_number}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' : v.status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{v.status}</span>
          </div>
          <div className="text-xs text-slate-500 mb-2">{v.passengers} {t('profile_vehicle_passengers')} | {v.luggage} {t('profile_vehicle_luggage')}</div>
          <div className="flex gap-2">
            <button onClick={() => startEdit(v)} className="flex-1 text-xs bg-slate-700 text-slate-300 py-2 rounded-lg active:bg-slate-600" data-testid={`pwa-edit-vehicle-${v.id}`}>{t('common_edit')}</button>
            <button onClick={() => handleDelete(v.id)} className="text-xs bg-red-500/10 text-red-400 px-4 py-2 rounded-lg active:bg-red-500/20" data-testid={`pwa-delete-vehicle-${v.id}`}>{t('common_delete')}</button>
          </div>
        </div>
      ))}

      {vehicles.length < 4 && !showAdd && !editVehicle && (
        <button onClick={() => { setShowAdd(true); resetForm(); setError(''); }} className="w-full border-2 border-dashed border-slate-700 rounded-xl py-4 text-slate-400 active:border-orange-500 active:text-orange-400 flex items-center justify-center gap-2 text-sm" data-testid="pwa-add-vehicle-btn">
          <span className="text-lg">+</span> {t('company_add_vehicle')}
        </button>
      )}
    </div>
  );
}

// --- Document Upload Screen ---
export function DocumentsScreen({ t, Icons, driverProfile, onBack, onRefresh }) {
  const [uploading, setUploading] = useState(null);
  const [docsStatus, setDocsStatus] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null); // { type, vehicleId?, docType }

  React.useEffect(() => {
    api.get('/driver/documents-status').then(r => setDocsStatus(r.data)).catch(() => {});
  }, []);

  const toBase64 = (file) => compressIfImage(file);

  const triggerUpload = (type, docType, vehicleId) => {
    setUploadTarget({ type, docType, vehicleId });
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget.docType); setError('');
    try {
      const base64 = await toBase64(file);
      if (uploadTarget.type === 'personal') {
        await api.post(`/driver/upload-document/${uploadTarget.docType}`, { document: base64 });
      } else {
        await api.post(`/driver/vehicle/${uploadTarget.vehicleId}/upload-document/${uploadTarget.docType}`, { document: base64 });
      }
      const r = await api.get('/driver/documents-status');
      setDocsStatus(r.data);
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || t('common_error')); }
    finally { setUploading(null); setUploadTarget(null); e.target.value = ''; }
  };

  const personalDocs = [
    { key: 'id_passport', label: 'Personalausweis / Pass', required: true },
    { key: 'selfie_with_license', label: t('driver_docs_selfie'), required: true },
    { key: 'transport_license', label: t('driver_docs_license'), required: false },
    { key: 'criminal_record', label: 'Strafregisterauszug', required: false },
  ];

  const vehicleDocs = [
    { key: 'vehicle_registration', label: t('driver_docs_registration'), required: true },
    { key: 'vehicle_photo', label: t('driver_docs_vehicle_photo'), required: true },
    { key: 'insurance', label: t('driver_docs_insurance'), required: true },
  ];

  const isUploaded = (type, key, vehicleId) => {
    if (!docsStatus) return false;
    if (type === 'personal') return docsStatus.personal_documents?.[key] || false;
    const v = docsStatus.vehicles?.find(x => x.vehicle_id === vehicleId);
    return v ? !v.missing?.includes(key) : false;
  };

  const triggerCamera = (type, docType, vehicleId) => {
    setUploadTarget({ type, docType, vehicleId });
    // Create a temporary file input for camera
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = handleFile;
    input.click();
  };

  const triggerGallery = (type, docType, vehicleId) => {
    setUploadTarget({ type, docType, vehicleId });
    // Create a temporary file input for gallery
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = handleFile;
    input.click();
  };

  const DocRow = ({ doc, type, vehicleId }) => {
    const uploaded = isUploaded(type, doc.key, vehicleId);
    const isCurrentlyUploading = uploading === doc.key;
    return (
      <div className="bg-slate-900 border border-white/10 p-4 mb-3" data-testid={`pwa-doc-${doc.key}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${uploaded ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
            {isCurrentlyUploading ? <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" /> : uploaded ? <Icons.Check /> : <Icons.Camera />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-medium">{doc.label}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{uploaded ? 'Hochgeladen' : doc.required ? 'Pflicht' : 'Optional'}</div>
          </div>
          {uploaded && <span className="text-emerald-400 text-xs font-medium px-2 py-1 bg-emerald-500/10">OK</span>}
        </div>
        {/* Camera / Gallery Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => triggerCamera(type, doc.key, vehicleId)} disabled={isCurrentlyUploading}
            className="flex flex-col items-center justify-center p-4 border border-orange-500/30 bg-orange-500/5 active:scale-95 transition-transform active:bg-orange-500/10" data-testid={`pwa-camera-${doc.key}`}>
            <Icons.Camera />
            <span className="text-[10px] text-orange-400 mt-1.5 font-medium uppercase tracking-wider">Kamera</span>
          </button>
          <button onClick={() => triggerGallery(type, doc.key, vehicleId)} disabled={isCurrentlyUploading}
            className="flex flex-col items-center justify-center p-4 border border-white/10 bg-slate-800 active:scale-95 transition-transform active:bg-white/5" data-testid={`pwa-gallery-${doc.key}`}>
            <Icons.Upload />
            <span className="text-[10px] text-slate-400 mt-1.5 font-medium uppercase tracking-wider">Galerie</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="pb-4" data-testid="pwa-documents-screen">
      <input type="file" ref={fileRef} className="hidden" accept="image/*" capture="environment" onChange={handleFile} />
      <button onClick={onBack} className="flex items-center gap-1.5 text-slate-400 mb-5 active:text-white py-2 text-sm font-medium"><Icons.ChevronLeft />{t('common_back')}</button>
      <h2 className="text-white font-bold text-xl tracking-tight mb-1">{t('driver_docs_title')}</h2>
      <p className="text-slate-500 text-xs mb-5">Laden Sie Ihre Dokumente hoch</p>
      {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-3 mb-4">{error}</div>}

      {/* Personal Documents */}
      <h3 className="text-slate-500 text-[10px] uppercase tracking-widest font-medium mb-3">Persönliche Dokumente</h3>
      {personalDocs.map(doc => <DocRow key={doc.key} doc={doc} type="personal" />)}

      {/* Vehicle Documents */}
      {(driverProfile?.vehicles || []).map(v => (
        <div key={v.id} className="mt-6">
          <h3 className="text-slate-500 text-[10px] uppercase tracking-widest font-medium mb-3">{v.make} {v.model} — {v.plate_number}</h3>
          {vehicleDocs.map(doc => <DocRow key={doc.key} doc={doc} type="vehicle" vehicleId={v.id} />)}
        </div>
      ))}

      {docsStatus?.complete && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm p-4 text-center mt-4 font-medium" data-testid="pwa-docs-complete">
          {t('driver_docs_all_complete')}
        </div>
      )}
    </div>
  );
}
